import { Modal } from '@mantine/core';
import useUpdatePlaceMetadata from '../../../hooks/places/useUpdatePlaceMetadata.ts';
import { PlaceWithServices } from '../../../models/Place.ts';
import MutatePlaceForm, {
  MutatePlaceFormValues,
} from '../../ui/places/MutatePlaceForm.tsx';
import { UpdatePlaceMetadataRequest } from '../../../api/requests/place-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';
import { parseGoogleStreetViewUrl } from '../../../utils/googleStreetViewUrl.ts';

interface UpdatePlaceMetadataModalProps {
  opened: boolean;
  onClose: () => void;
  place: PlaceWithServices;
  onSuccess?: () => void;
}

const UpdatePlaceMetadataModal = ({
  opened,
  onClose,
  place,
  onSuccess,
}: UpdatePlaceMetadataModalProps) => {
  const { mutateAsync: updatePlaceMetadata } = useUpdatePlaceMetadata();

  const { mutate: handleSubmit, isPending: isUpdatePlaceMetadataPending } =
    useMutation<void, ErrorResponse, MutatePlaceFormValues>({
      mutationFn: async (formValues) => {
        const { lat, lng, heading, pitch, fov } = parseGoogleStreetViewUrl(
          formValues.googleMapStreetViewUrl,
        );
        const updatePlaceMetadataRequest: UpdatePlaceMetadataRequest = {
          placeId: place.id,
          name: formValues.name.trim(),
          lat: lat,
          lng: lng,
          address: formValues.address.trim(),
          googleStreetViewURL: formValues.googleMapStreetViewUrl.trim(),
          googleStreetViewLat: lat,
          googleStreetViewLng: lng,
          googleStreetViewHeading: heading,
          googleStreetViewPitch: pitch,
          googleStreetViewFov: fov,
          notes: formValues.notes,
        };

        await updatePlaceMetadata(updatePlaceMetadataRequest);
      },
      onSuccess: () => {
        onSuccess?.();
      },
      onError: (error) => {
        if (error.code === 'INVALID_INPUT') {
          notifyError('更新地點失敗', '請檢查輸入內容是否有誤');
        } else {
          notifyError('', error.message);
        }
      },
    });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯地點"
      centered
      closeOnClickOutside={false}
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <MutatePlaceForm
        initialValues={{
          name: place.name,
          address: place.address,
          googleMapStreetViewUrl: place.googleStreetViewInfo.url,
          notes: place.notes,
        }}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isUpdatePlaceMetadataPending}
        submitButtonText={'更新'}
      />
    </Modal>
  );
};

export { UpdatePlaceMetadataModal };
